import type { ClientSlidingScale, SlidingMode, SlidingTier } from '@ehr/shared';
import { addYearIso, discountLabel, normalizeClientSliding, slidingFor } from '@ehr/shared';

/**
 * ehr#375 (Jay 10-30): "The practice should auto-populate down to the client chart. If it's modified
 * in demographics, then that needs to be the amount put on invoices. Every client potentially can
 * have a different amount."
 *
 * The chart's sliding scale reads like the practice's tier table: Tier, Discount type and Amount.
 * Picking a tier fills the type and amount from Data & settings; changing them makes it this
 * client's own amount (what invoices use). An amount left at the tier's is saved as the tier, so a
 * later change to the tier reaches the client. Stored as before ({ tierId, own, ownAmount, ... }).
 */
export interface SlidingDraft {
  /** '' = None (full fee) */
  tierId: string;
  mode: SlidingMode;
  amount: string;
  effective: string;
  reviewBy: string;
}
export type SlidingDraftField = 'amount' | 'effective' | 'reviewBy';
type TierLite = Pick<SlidingTier, 'id' | 'name' | 'mode' | 'amount'>;

/** Data & settings saved the tiers: an open chart re-reads them. */
export const SLIDING_TIERS_CHANGED = 'ehr:sliding-tiers-changed';

export const BLANK_SLIDING_DRAFT: SlidingDraft = {
  tierId: '',
  mode: 'pct',
  amount: '',
  effective: '',
  reviewBy: '',
};

const amt = (n: number | null | undefined) => (n == null ? '' : String(Number(n)));
const isOwn = (o: string): o is SlidingMode => o === 'pct' || o === 'usd';

/** The saved sliding scale as the chart shows it: a tier's type and amount filled in from the tier. */
export function draftFrom(s: ClientSlidingScale | null, tiers: readonly TierLite[]): SlidingDraft {
  if (!s) return BLANK_SLIDING_DRAFT;
  const t = s.tierId ? tiers.find((x) => x.id === s.tierId) : undefined;
  return {
    tierId: t ? t.id : '',
    mode: isOwn(s.own) ? s.own : t ? t.mode : 'pct',
    amount: isOwn(s.own) ? amt(s.ownAmount) : t ? amt(t.amount) : '',
    effective: s.effective ?? '',
    reviewBy: s.reviewBy ?? '',
  };
}

/** What Save sends (PUT /contacts/:id/sliding-scale). */
export function bodyFrom(d: SlidingDraft, tiers: readonly TierLite[]) {
  const typed = d.amount.trim();
  const n = Number(typed);
  const tierId = d.tierId || null;
  // No tier and no amount = the full fee. A tier with the amount cleared asks for an amount.
  const own = typed === '' && !tierId ? ('tier' as const) : d.mode;
  return normalizeClientSliding(
    {
      tierId,
      own,
      ownAmount: typed === '' || !Number.isFinite(n) ? null : n,
      effective: d.effective || null,
      reviewBy: d.reviewBy || null,
    },
    tiers,
  );
}

/** Picking a tier, or using something (a tier or an amount) for the first time, fills the dates. */
function withDates(d: SlidingDraft, today: string): SlidingDraft {
  if ((d.tierId || d.amount.trim()) && !d.effective)
    return { ...d, effective: today, reviewBy: d.reviewBy || addYearIso(today) };
  return d;
}

/** Picking a tier fills its discount type and amount; None clears the amount (the full fee). */
export function withTier(
  d: SlidingDraft,
  tiers: readonly TierLite[],
  tierId: string,
  today: string,
): SlidingDraft {
  const t = tiers.find((x) => x.id === tierId);
  if (!t) return { ...d, tierId: '', amount: '' };
  return withDates({ ...d, tierId: t.id, mode: t.mode, amount: amt(t.amount) }, today);
}

export function withAmount(d: SlidingDraft, typed: string, today: string): SlidingDraft {
  // digits and one dot
  const [whole = '', ...rest] = typed.replace(/[^0-9.]/g, '').split('.');
  const clean = rest.length ? `${whole}.${rest.join('')}` : whole;
  return withDates({ ...d, amount: clean }, today);
}

export function withMode(d: SlidingDraft, mode: SlidingMode): SlidingDraft {
  return { ...d, mode };
}

/** Back to the tier's own type and amount ("Use Tier 1's amount"). */
export function backToTier(d: SlidingDraft, tiers: readonly TierLite[]): SlidingDraft {
  const t = tiers.find((x) => x.id === d.tierId);
  return t ? { ...d, mode: t.mode, amount: amt(t.amount) } : d;
}

/** The line under Amount: where the amount came from. */
export function amountNote(
  d: SlidingDraft,
  tiers: readonly TierLite[],
): { text: string; reset?: string } | null {
  const t = d.tierId ? tiers.find((x) => x.id === d.tierId) : undefined;
  const b = bodyFrom(d, tiers);
  if (t && b.own === 'tier') return { text: `${t.name}'s amount, from Data & settings.` };
  if (t)
    return {
      text: `This client only. ${t.name} is ${discountLabel(t.mode, t.amount)}.`,
      reset: `Use ${t.name}'s amount`,
    };
  if (d.amount.trim()) return { text: 'This client only.' };
  return null;
}

/** The green "On invoices" line: this client's own amount, else the tier, else the full fee. */
export function onInvoicesLabel(d: SlidingDraft, tiers: readonly TierLite[]): string {
  const b = bodyFrom(d, tiers);
  const a = slidingFor(
    { ...b, ownAmount: b.ownAmount ?? null, effective: null, reviewBy: null },
    tiers,
    '',
  );
  return a ? a.label : 'Full fee';
}

/** The first problem per field, from the shared schema's paths. */
export function draftErrors(
  issues: readonly { path: readonly PropertyKey[]; message: string }[],
): Partial<Record<SlidingDraftField, string>> {
  const e: Partial<Record<SlidingDraftField, string>> = {};
  for (const i of issues) {
    const p = String(i.path[0]);
    const k: SlidingDraftField | null =
      p === 'ownAmount' ? 'amount' : p === 'effective' || p === 'reviewBy' ? p : null;
    if (k && !e[k]) e[k] = i.message;
  }
  return e;
}
